import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import classList from 'flarum/common/utils/classList';
import Button from 'flarum/common/components/Button';
import CommentPost from 'flarum/forum/components/CommentPost';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';
import GatePrompt from '../common/components/GatePrompt';
import { t } from '../common/t';
import { act, primaryAction } from './act';
const gateOf = post => post.attribute('velvetRope') || null;
const meterOf = post => post.discussion()?.attribute('velvetRopeMeter') || null;

/**
 * Give each locked placeholder the server left in a post its label and make
 * it a button. Runs when a post is drawn or redrawn, so posts that arrive
 * later (more replies loaded, a realtime reply) are covered too.
 */
function labelLocks(element, gate) {
  if (!element || !gate) return;
  element.querySelectorAll('.VelvetRope-lock:not([data-ready])').forEach(lock => {
    const text = String(t(`lib.lock.${gate.reason === 'meter' ? 'guest' : gate.reason}.${lock.dataset.kind}`));
    const icon = document.createElement('i');
    icon.className = 'fas fa-lock';
    icon.setAttribute('aria-hidden', 'true');
    lock.prepend(icon);
    const label = document.createElement('span');
    label.className = 'VelvetRope-lock-label';
    label.textContent = text;
    lock.append(label);
    lock.setAttribute('role', 'button');
    lock.setAttribute('tabindex', '0');
    lock.dataset.ready = '';
    lock.dataset.reason = gate.reason;
    if (gate.upgradeUrl) lock.dataset.upgrade = gate.upgradeUrl;
  });
}

/**
 * Whether this is the first gated post in view: the nearest loaded comment
 * before it (event posts such as renames don't count) was read in full, or
 * nothing before it is loaded.
 */
function firstGated(post) {
  const number = post.number();
  const before = (post.discussion()?.posts() || []).filter(p => p && p.contentType() === 'comment' && p.number() < number).sort((a, b) => b.number() - a.number())[0];
  const gate = before ? gateOf(before) : null;
  return !before || !gate || gate.mode === 'lock';
}
const refetched = new Set();

/** Free discussions left: say so once per discussion, when few are. */
const warned = new Set();
function meterNotice(discussion) {
  const meter = discussion.attribute('velvetRopeMeter');
  const id = discussion.id();
  if (!meter || !meter.counted || !id || warned.has(id) || meter.remaining > meter.warn) return;
  warned.add(id);
  const text = meter.remaining === 0 ? t(`forum.meter.last_${meter.period}`) : t(`forum.meter.left_${meter.period}`, {
    count: meter.remaining
  });
  const gate = {
    mode: 'lead',
    reason: 'meter'
  };
  app.alerts.show({
    type: 'warning',
    controls: [m(Button, {
      className: "Button Button--link",
      onclick: () => act(primaryAction(gate), gate)
    }, app.forum.attribute('allowSignUp') ? t('lib.prompt.sign_up') : t('lib.prompt.log_in'))]
  }, text);
}
app.initializers.add('ernestdefoe-velvet-rope', () => {
  extend(CommentPost.prototype, 'elementAttrs', function (attrs) {
    const gate = gateOf(this.attrs.post);
    if (gate) attrs.className = classList(attrs.className, 'VelvetRope-post', `VelvetRope-post--${gate.mode}`);
  });
  extend(CommentPost.prototype, 'content', function (items) {
    const post = this.attrs.post;
    const gate = gateOf(post);

    // Locked media explains itself; a preview or a hidden reply gets the
    // prompt: in full on the first gated post in view, one line after it.
    if (gate && gate.mode !== 'lock') {
      items.push(m(GatePrompt, {
        gate: gate,
        meter: meterOf(post),
        compact: !firstGated(post),
        onaction: act
      }));
    }
  });
  extend(CommentPost.prototype, ['oncreate', 'onupdate'], function () {
    const post = this.attrs.post;
    const gate = gateOf(post);
    labelLocks(this.element, gate);

    // A post rendered for someone else: a realtime reply, pushed once to
    // everyone as a guest sees it. Fetch it again as this reader, once.
    if (gate && (gate.generic || gate.reason === 'guest' && app.session.user) && !refetched.has(post.id())) {
      refetched.add(post.id());
      app.store.find('posts', post.id()).then(() => m.redraw());
    }
  });
  extend(DiscussionPage.prototype, 'show', function (_, discussion) {
    meterNotice(discussion);
  });

  // One listener for every locked placeholder on the page, present or future.
  const onLock = e => {
    const lock = e.target?.closest?.('.VelvetRope-lock[data-ready]');
    if (!lock || e instanceof KeyboardEvent && e.key !== 'Enter' && e.key !== ' ') return;

    // Read from the placeholder itself: it may be in a post list, a search
    // result or a profile, outside any discussion's stream.
    const data = lock.dataset;
    const gate = {
      mode: 'lock',
      reason: data.reason,
      upgradeUrl: data.upgrade
    };
    e.preventDefault();
    act(primaryAction(gate), gate);
  };
  document.addEventListener('click', onLock);
  document.addEventListener('keydown', onLock);
});